import React, { useEffect, useState } from "react";
import { MdAttachFile } from "react-icons/md";

/**
 * Generic modal supporting text, textarea, select, checkbox, and file fields.
 */
export default function EditModal({ title, initial = {}, fields = [], onClose, onSave }) {
  const [form, setForm] = useState({ ...initial });
  const [showSuggest, setShowSuggest] = useState({});

  useEffect(() => setForm({ ...initial }), [initial]);

  const change = (name, value) => setForm((s) => ({ ...s, [name]: value }));

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-large">
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form className="modal-body" onSubmit={submit}>
          {fields.map((f) => {
            // Hide End Date if "Present" checkbox is checked
            if (f.name === "endYear" && form.present) return null;

            return (
              <div className="form-group" key={f.name}>
                {f.type !== "checkbox" && <label>{f.label}</label>}

                {/* ---------- Text Input ---------- */}
                {f.type === "text" && (
                  <div className="autosuggest-wrapper">
                    <input
                      type="text"
                      value={form[f.name] || ""}
                      placeholder={f.placeholder || ""}
                      onChange={(e) => {
                        change(f.name, e.target.value);
                        setShowSuggest((s) => ({
                          ...s,
                          [f.name]: e.target.value.length >= 1,
                        }));
                      }}
                      onFocus={(e) =>
                        setShowSuggest((s) => ({
                          ...s,
                          [f.name]: (e.target.value || "").length >= 1,
                        }))
                      }
                      onBlur={() =>
                        setTimeout(() => setShowSuggest((s) => ({ ...s, [f.name]: false })), 150)
                      }
                    />

                    {/* Suggestions */}
                    {f.suggestList && showSuggest[f.name] && (
                      <ul className="suggestions-list">
                        {f.suggestList
                          .filter(
                            (it) =>
                              (form[f.name] || "").length === 0 ||
                              (it ||"").toLowerCase().includes((form[f.name] || "").toLowerCase())
                          )
                          .slice(0, 8)
                          .map((sug) => (
                            <li key={sug} onMouseDown={() => change(f.name, sug)}>
                              {sug}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* ---------- Textarea ---------- */}
                {f.type === "textarea" && (
                  <textarea
                    value={form[f.name] || ""}
                    placeholder={f.placeholder || ""}
                    onChange={(e) => change(f.name, e.target.value)}
                    rows="3"
                  />
                )}

                {/* ---------- Select ---------- */}
                {f.type === "select" && (
                  <select
                    value={form[f.name] || ""}
                    onChange={(e) => change(f.name, e.target.value)}
                  >
                    <option value="">Select</option>
                    {(f.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                )}

                {/* ---------- Checkbox ---------- */}
                {f.type === "checkbox" && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!form[f.name]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        change(f.name, checked);
                        if (f.name === "present") {
                          setForm((s) => ({ ...s, present: checked }));
                        }
                      }}
                    />
                    {f.label}
                  </label>
                )}

                {/* ---------- File Upload ---------- */}
                {f.type === "file" && (
                  <div className="file-upload-wrapper">
                    <input
                      id={`file-${f.name}`}
                      type="file"
                      accept={f.accept || ".pdf"}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          change(f.name, file);
                          change(f.name + "Name", file.name);
                        }
                      }}
                    />
                    <label htmlFor={`file-${f.name}`} className="upload-btn">
                      {form[f.name + "Name"] ? "Change File" : "Upload PDF"}
                    </label>
                    {form[f.name + "Name"] && (
                      <small className="file-name"> <MdAttachFile/> {form[f.name + "Name"]}</small>
                    )}
                  </div>
                )}

                {f.hint && <small className="hint">{f.hint}</small>}
              </div>
            );
          })}

          <div className="modal-actions">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
